import { useCallback } from 'react';
import { usePositionContext, PositionContextFormValues } from '@/shared/context/PositionContext.tsx';
import {
    AutomaticProductsForm,
    AutomaticProductsFormProps,
} from '@/shared/forms/AutomaticProductsForm/AutomaticProductsForm.tsx';
import { CustomerInfo } from '@/shared/models/ContextDataModel/interfaces/entities/customer-info.ts';
import { PRICING_STRATEGY, PRODUCT_TYPES } from '@schuettflix/interfaces';
import { usePositionV2 } from '@/clients/position/usePositionV2.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { isAdditionalServiceProduct } from '@/shared/domain/product/isAdditionalServiceProduct.ts';
import { getCommittedProductsFormRules } from '@/modules/position/domain/getCommittedProductsFormRules.ts';
import { toAutomaticUnit } from '@/shared/forms/AutomaticProductsForm/utils/toAutomaticUnit.ts';

type UnifiedAutomaticPositionProductSelectionProps = {
    onContinue: () => void;
    onClearSelection: () => void;
};

export function UnifiedAutomaticPositionProductSelection({
    onContinue,
    onClearSelection,
}: UnifiedAutomaticPositionProductSelectionProps) {
    const { isEditing, positionId, contextData } = usePositionContext();

    const formData = contextData.getFormData();
    const businessCase = contextData.getBusinessCase();
    const customerInfo = contextData.getCustomerInformation();

    const productType = businessCase?.productType ?? formData.productType;
    const isStandalone = businessCase?.isStandalone ?? formData.isStandalone ?? false;

    const handleSubmit: AutomaticProductsFormProps['onSubmit'] = useCallback(
        ({ values, businessCase, productConfigurations }) => {
            contextData.setFormData(values);
            contextData.setBusinessCase(businessCase);
            contextData.setProductConfigurations(productConfigurations);
            onContinue();
        },
        [contextData, onContinue]
    );

    if (!productType || !customerInfo) {
        return;
    }

    if (isEditing && businessCase) {
        return (
            <EditUnifiedAutomaticPositionProducts
                positionId={positionId}
                productType={productType}
                isStandalone={isStandalone}
                pricingStrategy={businessCase.pricingStrategy}
                defaultValues={formData}
                customerInfo={customerInfo}
                onSubmit={handleSubmit}
                onClearSelection={onClearSelection}
            />
        );
    } else {
        return (
            <AutomaticProductsForm
                scope="POSITION"
                productType={productType}
                isStandalone={isStandalone}
                pricingStrategy={businessCase?.pricingStrategy}
                defaultValues={formData}
                constructionProjectId={customerInfo.constructionProjectId}
                customerOrganizationId={customerInfo.organizationId}
                mode="create"
                onSubmit={handleSubmit}
                onClearSelection={onClearSelection}
            />
        );
    }
}

type EditUnifiedAutomaticPositionProductsProps = {
    positionId: string;
    productType: PRODUCT_TYPES;
    isStandalone: boolean;
    pricingStrategy: PRICING_STRATEGY;
    defaultValues: PositionContextFormValues;
    customerInfo: CustomerInfo;
    onSubmit: AutomaticProductsFormProps['onSubmit'];
    onClearSelection: () => void;
};

const EditUnifiedAutomaticPositionProducts = ({
    positionId,
    productType,
    isStandalone,
    pricingStrategy,
    defaultValues,
    customerInfo,
    onSubmit,
    onClearSelection,
}: EditUnifiedAutomaticPositionProductsProps) => {
    const { data: position } = usePositionV2(positionId);
    const mainProduct = position.productConfigurations.find(product => product.isMainProduct);
    const additionalServices = position.productConfigurations.filter(isAdditionalServiceProduct);

    if (!mainProduct) {
        throw new Error(
            `position ${position.number} doesn't have a main product, required to render EditUnifiedAutomaticPositionProducts`
        );
    }

    const hasOrders = position.orderCount > 0;
    const orderedSalesQuantity = mainProduct.fulfilledQuantity + mainProduct.reservedQuantity;
    // Platform admins can order beyond a position's quantity, so orderedQuantity may exceed it.
    // Cap the floor at the current salesQuantity, otherwise resubmitting it unchanged would fail validation.
    const minimumSalesQuantity = Math.min(orderedSalesQuantity, mainProduct.salesQuantity);

    const isTransportOnly = isTransportProduct(productType);
    const salesUnit = toAutomaticUnit(mainProduct.salesUnit);

    return (
        <AutomaticProductsForm
            scope="POSITION"
            productType={productType}
            pricingStrategy={pricingStrategy}
            isStandalone={isStandalone}
            defaultValues={defaultValues}
            constructionProjectId={customerInfo.constructionProjectId}
            customerOrganizationId={customerInfo.organizationId}
            mode="edit"
            onSubmit={onSubmit}
            onClearSelection={onClearSelection}
            formRules={{
                salesMaterialQuantity: {
                    minValue: isTransportOnly ? undefined : minimumSalesQuantity,
                    unit: isTransportOnly ? undefined : salesUnit,
                },
                salesTransportQuantity: {
                    minValue: isTransportOnly ? minimumSalesQuantity : undefined,
                    unit: isTransportOnly ? salesUnit : undefined,
                },
                freightType: {
                    disabled: hasOrders,
                },
                disposalPurpose: {
                    disabled: hasOrders,
                },
                salesMaterialUnit: {
                    disabled: hasOrders,
                },
                salesTransportUnit: {
                    disabled: hasOrders,
                },
                salesMaterialTaxClassId: {
                    disabled: hasOrders,
                },
                salesTransportTaxClassId: {
                    disabled: hasOrders,
                },
                additionalServices: getCommittedProductsFormRules(additionalServices, rule => ({
                    salesQuantity: { minValue: rule.salesQuantity.minValue, unit: rule.salesQuantity.unit },
                    salesUnit: { disabled: rule.disableSalesUnit },
                    disableDelete: rule.disableDelete,
                })),
                pricingStrategy: {
                    disabled: hasOrders,
                },
                isSelectionClearingDisabled: hasOrders,
            }}
        />
    );
};
